import { FOUNDATION } from 'config/features';
import ERROR_CODES from './errorCodes';

const getErrorCodeForHTTPStatusCode = statusCode => {
  if (statusCode === 401) {
    return ERROR_CODES.HTTP_RESPONSE_STATUS_UNAUTHORIZED;
  } else {
    return ERROR_CODES.HTTP_RESPONSE_STATUS_NOT_2XX;
  }
};

const tryToParseAsJsonResponse = async response => {
  // Header may have character set appended at the end.
  const contentTypeHeader = response.headers.get('Content-Type');
  if (!contentTypeHeader?.startsWith('application/json')) {
    throw new Error(ERROR_CODES.HTTP_RESPONSE_INVALID_CONTENT_TYPE);
  }

  return response.json();
};

// Check if the response is a redirect to the logout page
// This is used to handle logout redirects in the application
// If the response URL ends with '/ui/login/logout/done', it indicates a logout redirect
// and we redirect the user to that URL.
// This is particularly useful in applications where the backend may redirect to a logout page
// after a session timeout or when the user is no longer authenticated.
const isLogoutRedirect = response => {
  if (response.url.includes('/ui/login/logout/done') || response.url.includes('/oidc/v1/end_session')) {
    globalThis.location.href = response.url;
    return true;
  }
  return false;
};

const checkStatusCode = async (response, returnErrorMessage) => {
  if (response.ok) {
    return;
  }

  let error = new Error(getErrorCodeForHTTPStatusCode(response.status));
  if (returnErrorMessage) {
    try {
      const serverError = await tryToParseAsJsonResponse(response);
      if (serverError.message) {
        error = new Error(serverError.message);
      }
    } catch {}
  }

  throw error;
};

const fetchJSON = async (url, options) => {
  const {
    method = 'GET',
    body,
    stringifyBody = true,
    token,
    parseResult = true,
    returnErrorMessage = true,
    contentType,
    headers,
    signal,
  } = options;

  const mappedOptions = {
    headers: {
      'Content-Type': contentType || 'application/json',
      Accept: 'application/json',
      credentials: 'include',
      ...(token && !FOUNDATION ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    method,
    ...(body && { body: stringifyBody ? JSON.stringify(body) : body }),
    signal,
  };

  const response = await fetch(url, mappedOptions);

  await checkStatusCode(response, returnErrorMessage);

  if (isLogoutRedirect(response)) {
    return;
  }

  return parseResult ? tryToParseAsJsonResponse(response) : response;
};

export const get = fetchJSON;

export const patch = (url, options) => fetchJSON(url, { method: 'PATCH', ...options });

export const post = (url, options) => fetchJSON(url, { method: 'POST', ...options });

export const put = (url, options) => fetchJSON(url, { method: 'PUT', ...options });

export const del = (url, options) => fetchJSON(url, { method: 'DELETE', parseResult: false, ...options });
